import { Text, View, XStack } from 'tamagui';

/**
 * Tabs — a single-select segmented control (molecule).
 *
 * The "All experiences" filter from the home page: a recessed track holding
 * equal-width segments; the active one lifts out of the track as a lighter,
 * raised surface. One segment is always selected.
 *
 * Generic on purpose — takes `options` + `value` + `onChange` for any string
 * key set, rather than being tied to one filter enum, so it's reusable wherever
 * a segmented control is needed. RN text doesn't cascade, so each segment sets
 * its own label color/weight off whether it's active.
 */
type TabOption<T extends string> = {
  key: T;
  label: string;
};

type TabsProps<T extends string> = {
  options: readonly TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
};

export default function Tabs<T extends string>({
  options,
  value,
  onChange,
}: TabsProps<T>) {
  return (
    <XStack
      alignSelf="stretch"
      backgroundColor="$backgroundSecondary"
      rounded="$base"
      p="$xs"
      gap="$xs"
    >
      {options.map((option) => {
        const active = option.key === value;
        return (
          <View
            key={option.key}
            grow={1}
            flexBasis="auto"
            onPress={() => onChange(option.key)}
            cursor="pointer"
            rounded="$base"
            py="$sm"
            px="$xs"
            items="center"
            backgroundColor={active ? '$background' : '$transparent'}
          >
            <Text
              fontFamily="$body"
              fontWeight={active ? '$semibold' : '$medium'}
              fontSize="$small"
              numberOfLines={1}
              color="$textDark"
              opacity={active ? 1 : 0.55}
            >
              {option.label}
            </Text>
          </View>
        );
      })}
    </XStack>
  );
}
